import { useState, useEffect, useCallback, useRef } from 'react';
import {
  DialogContent, DialogActions, Button, Alert, Box, CircularProgress,
  Typography, IconButton, MenuItem, Select, FormControl,
  InputLabel, SelectChangeEvent, Chip, Autocomplete, TextField,
} from '@mui/material';
import DraggableDialog from '../../components/DraggableDialog';
import { CopyId } from '../../components/CopyId';
import { Add, Delete, Sync } from '@mui/icons-material';
import { Formik , FormikProps } from 'formik';
import * as Yup from 'yup';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useModalState } from '../../hooks/useModalState';
import { useDirtyFormGuard } from '../../hooks/useDirtyFormGuard';
import FormDirtyTracker from '../../components/FormDirtyTracker';
import { FormikSwitch } from '../../components/FormikFields';
import { tsIntegrationsService, TSIntegrationConfig, TaboolaCampaignGroup } from '../../services/ts-integrations.service';
import { trafficSourcesService, TrafficSource } from '../../services/traffic-sources.service';
import { FormikTextField } from '../../components/FormikFields';


/** Popup window names, which are also the message types their callback posts back. */
const OAUTH_CONNECT_TYPES = ['google_ads_connect', 'microsoft_ads_connect'] as const;

interface AdAccountEntry {
  id: string;
  name: string;
  timezone: string;
}

const TWITTER_CURRENCIES =['USD', 'ILS', 'EUR', 'GBP', 'CAD', 'AUD', 'NZD', 'JPY', 'CHF', 'SEK', 'NOK', 'DKK', 'BRL', 'MXN', 'INR', 'ZAR', 'TRY', 'PLN', 'SGD', 'HKD'];

const INTEGRATION_KINDS = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'google_ads', label: 'Google Ads' },
  { value: 'bing', label: 'Microsoft Advertising (Bing)' },
  { value: 'taboola', label: 'Taboola' },
  { value: 'newsbreak', label: 'NewsBreak' },
  { value: 'twitter', label: 'X (Twitter)' },
];

const DEFAULT_CONFIG: TSIntegrationConfig = {
  credentials: {},
  sync_cost: { enabled: true },
};

interface TSIntegrationFormValues {
  kind: string;
  traffic_source_id: string;
  enabled: boolean;
  sync_cost_enabled: boolean;
  config: TSIntegrationConfig;
  ad_accounts: AdAccountEntry[];
}

const schema = Yup.object({
  kind: Yup.string().required('Kind is required'),
  traffic_source_id: Yup.string().required('Traffic source is required'),
  enabled: Yup.boolean(),
});

export default function CreateEditTSIntegration() {
  const { modalState, toggleModal } = useModalState('ts-integration');
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const close = useCallback(() => toggleModal(false), [toggleModal]);
  const { guardedClose, setDirty, setSubmitted } = useDirtyFormGuard(close);

  const { data: entity, isLoading, refetch } = useQuery({
    queryKey: ['ts-integration', modalState.id],
    queryFn: () => tsIntegrationsService.getById(modalState.id!),
    enabled: !!modalState.id && modalState.open,
  });

  const { data: trafficSources } = useQuery({
    queryKey: ['traffic-sources-all'],
    queryFn: () => trafficSourcesService.query({ limit: 200 }),
    enabled: modalState.open,
  });

  const isEdit = modalState.reason === 'Update' && !!entity;
  const title = isEdit ? `Edit Integration: ${entity?.traffic_source?.name || entity?.id}` : 'Create Integration';

  // The OAuth popup and the fetch-accounts call land outside the render prop.
  const formik = useRef<FormikProps<TSIntegrationFormValues>>(null);
  const setConfig = (next: TSIntegrationConfig) => formik.current?.setFieldValue('config', next);
  const setAdAccounts = (next: AdAccountEntry[]) => formik.current?.setFieldValue('ad_accounts', next);
  const config = () => formik.current?.values.config ?? DEFAULT_CONFIG;

  useEffect(() => { if (modalState.id && modalState.open) refetch(); }, [modalState.id, modalState.open, refetch]);

  const handleAddAdAccount = () => {
    setAdAccounts([...(formik.current?.values.ad_accounts ?? []), { id: '', name: '', timezone: 'America/New_York' }]);
  };

  const handleRemoveAdAccount = (index: number) => {
    setAdAccounts((formik.current?.values.ad_accounts ?? []).filter((_, i) => i !== index));
  };

  const [googleConnected, setGoogleConnected] = useState(false);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!OAUTH_CONNECT_TYPES.includes(event.data?.type)) return;
      if (event.data.success && event.data.refresh_token) {
        setConfig({
          ...config(),
          credentials: { ...config().credentials, refresh_token: event.data.refresh_token },
        });
        setGoogleConnected(true);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  useEffect(() => {
    if (entity?.config?.credentials?.refresh_token && entity?.kind === 'google_ads') {
      setGoogleConnected(true);
    }
  }, [entity]);

  const [fetchingAccounts, setFetchingAccounts] = useState(false);

  const handleFetchAccounts = async () => {
    const token = config().credentials?.access_token;
    if (!token) {
      enqueueSnackbar('Enter an access token first', { variant: 'warning' });
      return;
    }
    setFetchingAccounts(true);
    try {
      const accounts = await tsIntegrationsService.fetchFbAdAccounts(token);
      setAdAccounts(accounts);
      enqueueSnackbar(`Fetched ${accounts.length} ad account(s)`, { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.error?.message || err.message || 'Failed to fetch accounts', { variant: 'error' });
    } finally {
      setFetchingAccounts(false);
    }
  };

  const [taboolaGroups, setTaboolaGroups] = useState<TaboolaCampaignGroup[] | null>(null);
  const [fetchingGroups, setFetchingGroups] = useState(false);
  // Bumped on every reset: a response for an earlier dialog or account is dropped.
  const groupsGen = useRef(0);

  const handleFetchTaboolaGroups = async (quiet = false) => {
    const c = config().credentials || {};
    if (!c.client_id || !c.client_secret || !c.account_id) {
      if (!quiet) enqueueSnackbar('Enter the client ID, secret and account ID first', { variant: 'warning' });
      return;
    }
    const gen = groupsGen.current;
    setFetchingGroups(true);
    try {
      const groups = await tsIntegrationsService.fetchTaboolaCampaignGroups({
        client_id: c.client_id, client_secret: c.client_secret, account_id: c.account_id,
      });
      if (gen === groupsGen.current) setTaboolaGroups(groups);
    } catch (err: any) {
      if (!quiet && gen === groupsGen.current) enqueueSnackbar(err?.response?.data?.error?.message || err.message || 'Failed to load campaign groups', { variant: 'error' });
    } finally {
      if (gen === groupsGen.current) setFetchingGroups(false);
    }
  };

  // The dialog stays mounted, so each opening starts clean.
  useEffect(() => {
    groupsGen.current++;
    setTaboolaGroups(null);
    setFetchingGroups(false);
    if (modalState.open && entity?.kind === 'taboola' && entity.config?.credentials?.client_secret) {
      // The entity's own credentials: formik's values may still hold the previous dialog's.
      const c = entity.config.credentials;
      const gen = groupsGen.current;
      tsIntegrationsService.fetchTaboolaCampaignGroups({ client_id: c.client_id, client_secret: c.client_secret, account_id: c.account_id })
        .then((groups) => { if (gen === groupsGen.current) setTaboolaGroups(groups); })
        .catch(() => {});
    }
  }, [modalState.open, entity]);

  const initialValues: TSIntegrationFormValues = entity
    ? {
      kind: entity.kind,
      traffic_source_id: entity.traffic_source_id,
      enabled: entity.enabled,
      sync_cost_enabled: entity.config?.sync_cost?.enabled ?? false,
      config: entity.config || DEFAULT_CONFIG,
      ad_accounts: entity.config?.credentials?.ad_accounts || [],
    }
    : {
      kind: 'facebook', traffic_source_id: '', enabled: true, sync_cost_enabled: true,
      config: DEFAULT_CONFIG, ad_accounts: [],
    };

  return (
    <DraggableDialog open={modalState.open} onClose={guardedClose} maxWidth="sm" fullWidth title={isLoading ? 'Loading...' : title} titleId="ts-integration-dialog-title">
      {isLoading && modalState.id ? (
        <DialogContent><Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box></DialogContent>
      ) : (
        <Formik
          innerRef={formik}
          initialValues={initialValues}
          enableReinitialize
          validationSchema={schema}
          onSubmit={async (values, { setSubmitting, setStatus }) => {
            try {
              setSubmitted();
              const credentials = { ...values.config.credentials };
              if (values.kind === 'facebook') {
                credentials.ad_accounts = values.ad_accounts;
              }
              const finalConfig: TSIntegrationConfig = {
                ...values.config,
                credentials,
                sync_cost: {
                  ...values.config.sync_cost,
                  enabled: values.sync_cost_enabled,
                  // The select's blank option is '', but the server reads absent as "use ad_id".
                },
              };

              const payload = {
                kind: values.kind,
                traffic_source_id: values.traffic_source_id,
                enabled: values.enabled,
                config: finalConfig,
              };

              if (isEdit) {
                await tsIntegrationsService.update(entity!.id, payload);
              } else {
                // Workspace is derived server-side from the chosen traffic source.
                await tsIntegrationsService.create(payload);
              }
              queryClient.invalidateQueries({ queryKey: ['ts-integrations'] });
              close();
              enqueueSnackbar('Saved', { variant: 'success' });
            } catch (err: any) {
              setStatus(err?.response?.data?.error?.message || err.message || 'Save failed');
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ submitForm, isSubmitting, status, values, setFieldValue }: FormikProps<TSIntegrationFormValues>) => (
            <>
              <FormDirtyTracker onDirtyChange={setDirty} />
              <DialogContent dividers>
                <CopyId id={entity?.id} show={isEdit} sx={{ mb: 1 }} />
                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Kind</InputLabel>
                    <Select
                      value={values.kind}
                      label="Kind"
                      onChange={(e: SelectChangeEvent) => setFieldValue('kind', e.target.value)}
                    >
                      {INTEGRATION_KINDS.map(k => (
                        <MenuItem key={k.value} value={k.value}>
                          {k.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" fullWidth>
                    <InputLabel>Traffic Source</InputLabel>
                    <Select
                      value={values.traffic_source_id}
                      label="Traffic Source"
                      onChange={(e: SelectChangeEvent) => setFieldValue('traffic_source_id', e.target.value)}
                    >
                      {(trafficSources?.data || [])
                        .filter((ts: TrafficSource) => ts.status === 'active')
                        .map((ts: TrafficSource) => (
                          <MenuItem key={ts.id} value={ts.id}>{ts.name}</MenuItem>
                        ))}
                    </Select>
                  </FormControl>

                  <FormikSwitch name="enabled" label="Enabled" />

                  {/* Facebook-specific credentials */}
                  {values.kind === 'facebook' && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 1 }}>Facebook Credentials</Typography>
                      <FormikTextField
                        label="System User Access Token"
                        size="small"
                        fullWidth
                        type="password"
                        name="config.credentials.access_token"
                      />

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                        <Typography variant="subtitle2">Ad Accounts</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Button size="small" startIcon={fetchingAccounts ? <CircularProgress size={14} /> : <Sync />}
                            onClick={handleFetchAccounts} disabled={fetchingAccounts || !values.config.credentials?.access_token}
                            variant="outlined">
                            {fetchingAccounts ? 'Fetching...' : 'Fetch from FB'}
                          </Button>
                          <Button size="small" startIcon={<Add />} onClick={handleAddAdAccount}>Add</Button>
                        </Box>
                      </Box>

                      {values.ad_accounts.map((_, idx) => (
                        <Box key={idx} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          <FormikTextField size="small" label="Account ID" placeholder="act_123456"
                            name={`ad_accounts.${idx}.id`}
                            sx={{ flex: 1 }} />
                          <FormikTextField size="small" label="Name"
                            name={`ad_accounts.${idx}.name`}
                            sx={{ flex: 1 }} />
                          <FormikTextField size="small" label="Timezone" placeholder="America/New_York"
                            name={`ad_accounts.${idx}.timezone`}
                            sx={{ flex: 1 }} />
                          <IconButton size="small" color="error" onClick={() => handleRemoveAdAccount(idx)}>
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      ))}
                    </>
                  )}

                  {/* Google Ads credentials */}
                  {values.kind === 'bing' && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 1 }}>Microsoft Advertising Credentials</Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {values.config.credentials?.refresh_token ? (
                          <Chip label="Connected" color="success" size="small" />
                        ) : null}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => window.open('/api/v1/auth/microsoft/connect-ads', 'microsoft_ads_connect', 'width=500,height=600,popup=yes')}
                        >
                          {values.config.credentials?.refresh_token ? 'Reconnect' : 'Connect with Google'}
                        </Button>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Microsoft Advertising accepts a Google sign-in, so this uses the same Google
                        account. It must be a user on the Bing account.
                      </Typography>
                      <FormikTextField
                        label="Developer Token" size="small" fullWidth
                        name="config.credentials.developer_token"
                        helperText="From ads.microsoft.com → Settings → Developer Settings. Stored with the integration."
                      />
                      <FormikTextField
                        label="Customer ID" size="small" fullWidth
                        name="config.credentials.customer_id"
                        helperText="The cid= in the Microsoft Advertising URL."
                      />
                      <FormikTextField
                        label="Account ID" size="small" fullWidth
                        name="config.credentials.account_id"
                        helperText="The aid= in the URL — not the eight-character account number."
                      />
                    </>
                  )}

                  {values.kind === 'google_ads' && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 1 }}>Google Ads Credentials</Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {googleConnected || values.config.credentials?.refresh_token ? (
                          <Chip label="Connected" color="success" size="small" />
                        ) : null}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => window.open('/api/v1/auth/google/connect-ads', 'google_ads_connect', 'width=500,height=600,popup=yes')}
                        >
                          {values.config.credentials?.refresh_token ? 'Reconnect Google' : 'Connect Google Account'}
                        </Button>
                      </Box>
                      <FormikTextField
                        label="Customer ID"
                        size="small"
                        fullWidth
                        placeholder="123-456-7890"
                        name="config.credentials.customer_id"
                      />
                      <FormikTextField
                        label="Manager Account ID (MCC)"
                        size="small"
                        fullWidth
                        placeholder="283-712-9442 (optional)"
                        name="config.credentials.login_customer_id"
                        helperText="Required if OAuth was done under a manager account"
                      />
                    </>
                  )}

                  {/* NewsBreak credentials — token from Ad Manager > Resources > API Access Tokens */}
                  {values.kind === 'newsbreak' && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 1 }}>NewsBreak Credentials</Typography>
                      <FormikTextField
                        label="Access Token"
                        size="small"
                        fullWidth
                        type="password"
                        name="config.credentials.access_token"
                        helperText="Ad Manager → Resources → API Access Tokens"
                      />
                      <FormikTextField
                        label="Ad Account ID (optional)"
                        size="small"
                        fullWidth
                        name="config.credentials.ad_account_id"
                        helperText="Limits the report to one ad account. Leave blank for all."
                      />
                      <FormikTextField
                        label="Report timezone"
                        size="small"
                        fullWidth
                        name="config.credentials.timezone"
                        helperText="IANA name, e.g. America/New_York. Must match the ad account, or spend lands on the wrong day. Blank = UTC."
                      />
                    </>
                  )}

                  {/* X (Twitter) credentials — OAuth 1.0a */}
                  {values.kind === 'twitter' && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 1 }}>X (Twitter) Credentials</Typography>
                      <FormikTextField
                        label="Consumer Key" size="small" fullWidth
                        name="config.credentials.consumer_key"
                      />
                      <FormikTextField
                        label="Consumer Secret" size="small" fullWidth type="password"
                        name="config.credentials.consumer_secret"
                      />
                      <FormikTextField
                        label="Access Token" size="small" fullWidth
                        name="config.credentials.access_token"
                      />
                      <FormikTextField
                        label="Access Token Secret" size="small" fullWidth type="password"
                        name="config.credentials.access_token_secret"
                      />
                      <FormikTextField
                        label="Ad Account ID" size="small" fullWidth
                        name="config.credentials.account_id"
                        helperText="The X Ads account id (ads-api :account_id)."
                      />
                      <FormikTextField
                        label="Report timezone" size="small" fullWidth
                        name="config.credentials.timezone"
                        helperText="IANA name, e.g. America/New_York. Must match the ad account. Blank = UTC."
                      />
                      <FormikTextField
                        select label="Billing currency" size="small" fullWidth
                        name="config.credentials.currency"
                        helperText="The currency the X account bills in. Spend is converted to USD at that day's rate."
                      >
                        {TWITTER_CURRENCIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                      </FormikTextField>
                    </>
                  )}

                  {/* Taboola credentials */}
                  {values.kind === 'taboola' && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 1 }}>Taboola Credentials</Typography>
                      <FormikTextField
                        label="Client ID"
                        size="small"
                        fullWidth
                        name="config.credentials.client_id"
                      />
                      <FormikTextField
                        label="Client Secret"
                        size="small"
                        fullWidth
                        type="password"
                        name="config.credentials.client_secret"
                      />
                      <FormikTextField
                        label="Account ID (Network)"
                        size="small"
                        fullWidth
                        name="config.credentials.account_id"
                        helperText="Network-level account ID from Taboola"
                        // Replaces formik's onBlur (props spread last), so mark touched here.
                        onBlur={() => {
                          formik.current?.setFieldTouched('config.credentials.account_id', true);
                          // Always: a load still in flight is for the account as it was.
                          groupsGen.current++; setTaboolaGroups(null); setFetchingGroups(false);
                        }}
                      />
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <Autocomplete
                          multiple
                          size="small"
                          sx={{ flex: 1 }}
                          options={taboolaGroups?.map((g) => g.id) ?? []}
                          value={values.config.campaign_group_ids ?? []}
                          onChange={(_, ids) => setConfig({ ...values.config, campaign_group_ids: ids })}
                          getOptionLabel={(id) => taboolaGroups?.find((g) => g.id === id)?.name ?? id}
                          renderInput={(params) => (
                            <TextField {...params} label="Campaign groups"
                              placeholder={values.config.campaign_group_ids?.length ? undefined : 'All campaign groups'}
                              helperText="Sync only these groups' cost. Required when another workspace syncs the same account." />
                          )}
                        />
                        <Button size="small" variant="outlined" sx={{ mt: 0.5, flexShrink: 0 }}
                          startIcon={fetchingGroups ? <CircularProgress size={14} /> : <Sync />}
                          onClick={() => handleFetchTaboolaGroups()} disabled={fetchingGroups}>
                          {taboolaGroups ? 'Reload' : 'Load groups'}
                        </Button>
                      </Box>
                    </>
                  )}

                  {/* Sync settings */}
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>Sync Settings</Typography>
                  <FormikSwitch name="sync_cost_enabled" label="Sync Cost Enabled" />

                  {/* Info display (read-only) */}
                  {isEdit && entity?.info?.sync_cost && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 1 }}>Sync Status</Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {entity.info.sync_cost.last_run && (
                          <Chip size="small" label={`Last run: ${new Date(entity.info.sync_cost.last_run).toLocaleString()}`} />
                        )}
                        {entity.info.sync_cost.done_until && (
                          <Chip size="small" label={`Done until: ${new Date(entity.info.sync_cost.done_until).toLocaleString()}`} />
                        )}
                        {entity.info.sync_cost.status_code && (
                          <Chip size="small"
                            color={entity.info.sync_cost.status_code === 'ok' ? 'success' : 'error'}
                            label={`Status: ${entity.info.sync_cost.status_code}`} />
                        )}
                        {entity.info.sync_cost.message && (
                          <Chip size="small" color="warning" label={entity.info.sync_cost.message} />
                        )}
                      </Box>
                    </>
                  )}

                  {status && <Alert severity="error">{status}</Alert>}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={guardedClose}>Cancel</Button>
                <Button variant="contained" onClick={submitForm} disabled={isSubmitting}>
                  {isSubmitting ? <CircularProgress size={20} /> : 'Save'}
                </Button>
              </DialogActions>
            </>
          )}
        </Formik>
      )}
    </DraggableDialog>
  );
}
